/**
 * Orquestração de recovery: detect → plan → authorize → execute → validate → commit → telemetry.
 */

import type { QueueItem } from '../queue/types';
import { mergeQueuesById } from '../coordination/reconciliation';
import {
  recoverStaleLeader,
  recoverInterruptedReplay,
  recoverInvalidIndex,
  recoverPartitionOverflow,
  recoverFailedShadowCommit,
  recoverCorruptedGeneration,
  recoverReplayCursorInvalid,
  recoverCorruptedReplaySnapshot,
  recoverReplayOwnershipMismatch,
  captureRecoverySnapshot,
  rollbackRecoverySnapshot,
} from './recoveryStrategies';
import { isPersistedLeader } from '../coordination/distributedRecovery';
import {
  acquireOperationContext,
  assertOperationValid,
} from '../coordination/ownershipGate';
import {
  eventTypeToQueueType,
  QUEUE_ITEM_TTL_MS,
  type QueueStatus,
} from '../queue/types';
import {
  recordRecoveryPhase,
  recordRecoveryRunComplete,
  recordRecoveryPlanned,
  recordRecoveryStarted,
  recordRecoveryFailed,
  recordRecoveryRollback,
} from '../telemetry';
import { enforcePartitionQueueQuotas } from '../queue/partitionPolicies';
import {
  rebuildQueueIndexFromArray,
  validateQueueIndex,
} from '../queue/queueIndex';
import { consumeReplayRecoveryScheduled } from '../replay/replayRecovery';
import {
  setRecoveryModeActive,
  clearRuntimeModeExplicit,
  getRuntimeMode,
} from '../runtime/runtimeMode';
import {
  escalateRecovery,
  getRecoveryLevel,
  _testResetRecoveryEscalation,
} from './recoveryEscalation';
import { reconcileAllSessionCursors } from '../replay/replayCursorReconciliation';

export type RecoveryPhase =
  | 'detect'
  | 'plan'
  | 'authorize'
  | 'execute'
  | 'validate'
  | 'commit'
  | 'telemetry';

export interface RecoveryPlanInput {
  localQueue: QueueItem[];
  loaded: QueueItem[];
  now: number;
  tabId: string;
  aliveTabIds: Set<string>;
  maxQueueItems?: number;
  corruptedGeneration?: number;
  shadowCommitFailed?: boolean;
}

export interface RecoveryPlanResult {
  items: QueueItem[];
  phases: RecoveryPhase[];
  committed: boolean;
  recoveryLevel: ReturnType<typeof getRecoveryLevel>;
}

function normalizeLoadedItem(i: QueueItem, now: number): QueueItem | null {
  if (!i || typeof i !== 'object' || !('params' in i)) return null;
  if (typeof i.attempts !== 'number') return null;
  if (now - (i.createdAt ?? 0) > QUEUE_ITEM_TTL_MS) return null;
  if (i.params.type === 'PAGE_VIEW') return null;
  return {
    ...i,
    type: i.type ?? eventTypeToQueueType(i.params.type),
    status: (i.status ?? 'queued') as QueueStatus,
    occurredAt: i.occurredAt ?? i.createdAt,
    sessionSequence: i.sessionSequence ?? 0,
  };
}

export function runRecoveryPlan(input: RecoveryPlanInput): RecoveryPlanResult {
  _testResetRecoveryEscalation();
  setRecoveryModeActive(true);
  const phases: RecoveryPhase[] = ['detect'];
  recordRecoveryPhase('detect');
  recordRecoveryPlanned();

  const normalized = input.loaded
    .map(i => normalizeLoadedItem(i, input.now))
    .filter((i): i is QueueItem => i != null);

  phases.push('plan');
  recordRecoveryPhase('plan');

  const maxItems = input.maxQueueItems ?? 100;
  const recoveryCtx = acquireOperationContext('recovery');

  phases.push('authorize');
  recordRecoveryPhase('authorize');
  const leaderOk =
    typeof window === 'undefined' || isPersistedLeader(input.tabId);
  const gateOk = assertOperationValid(recoveryCtx);
  if (!leaderOk || !gateOk) {
    recordRecoveryFailed(!leaderOk ? 'not_leader' : 'gate');
    phases.push('telemetry');
    recordRecoveryPhase('telemetry');
    setRecoveryModeActive(false);
    clearRuntimeModeExplicit();
    return {
      items: input.localQueue,
      phases,
      committed: false,
      recoveryLevel: 'soft',
    };
  }

  recordRecoveryStarted();
  const snapshot = captureRecoverySnapshot(input.localQueue);

  const replayScheduled = consumeReplayRecoveryScheduled();
  const cursorResults = reconcileAllSessionCursors([
    ...input.localQueue,
    ...normalized,
  ]);
  const cursorInvalid = cursorResults.some(r => r.action === 'recovery');
  const ownershipMismatch = recoverReplayOwnershipMismatch();

  escalateRecovery({
    replayInterrupted: replayScheduled,
    replayCursorInvalid: cursorInvalid,
    generationCorrupt: input.corruptedGeneration != null,
    shadowCommitFailed: input.shadowCommitFailed,
    replayOwnershipMismatch: ownershipMismatch,
    partitionOverflow: normalized.length > maxItems,
  });

  phases.push('execute');
  recordRecoveryPhase('execute');
  let working = mergeQueuesById(input.localQueue, normalized);
  working = recoverFailedShadowCommit(working);
  working = recoverStaleLeader(working, input.tabId, input.aliveTabIds);
  if (replayScheduled) {
    working = recoverInterruptedReplay(working);
  }
  working = recoverReplayCursorInvalid(working);
  if (getRecoveryLevel() === 'safe_mode') {
    recoverCorruptedReplaySnapshot();
  }
  working = recoverPartitionOverflow(working, maxItems);
  working = enforcePartitionQueueQuotas(working, maxItems);
  if (input.corruptedGeneration != null) {
    recoverCorruptedGeneration(input.corruptedGeneration);
  }

  phases.push('validate');
  recordRecoveryPhase('validate');
  recoverInvalidIndex(working);
  const indexValidation = validateQueueIndex(working);
  let indexValid = indexValidation.ok;
  if (!indexValid) {
    rebuildQueueIndexFromArray(working);
    indexValid = validateQueueIndex(working).ok;
  }

  const level = escalateRecovery({
    indexMismatch: !indexValidation.ok,
    validateFailed: !indexValid,
  });

  const committed =
    indexValid && level !== 'safe_mode' && getRuntimeMode() !== 'safe_mode';

  phases.push('commit');
  recordRecoveryPhase('commit');
  let items: QueueItem[];
  if (!committed) {
    recordRecoveryRollback();
    items = rollbackRecoverySnapshot(snapshot, input.localQueue);
    items = recoverPartitionOverflow(items, maxItems);
  } else {
    items = working;
  }

  phases.push('telemetry');
  recordRecoveryPhase('telemetry');
  recordRecoveryRunComplete();
  setRecoveryModeActive(false);
  if (getRuntimeMode() !== 'safe_mode') {
    clearRuntimeModeExplicit();
  }

  return { items, phases, committed, recoveryLevel: getRecoveryLevel() };
}
